import { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";

export interface Toast {
  id: number;
  message: string;
  kind?: "info" | "error";
}

interface ToastContextValue {
  toasts: Toast[];
  toast: (message: string, kind?: "info" | "error") => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

let _nextId = 1;

interface ToastProviderProps {
  children: ReactNode;
  // Milliseconds before auto-dismiss. Pass 0 to disable (used in tests so
  // the assertion isn't racing the wall-clock timer on slow CI runners).
  autoDismissMs?: number;
}

export function ToastProvider({ children, autoDismissMs = 3000 }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, kind: "info" | "error" = "info") => {
      const id = _nextId++;
      setToasts((list) => [...list, { id, message, kind }]);
      if (autoDismissMs > 0) {
        window.setTimeout(() => dismiss(id), autoDismissMs);
      }
    },
    [dismiss, autoDismissMs],
  );

  const value = useMemo(() => ({ toasts, toast, dismiss }), [toasts, toast, dismiss]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-container" aria-live="polite" aria-atomic="true">
        {toasts.map((t) => (
          <div key={t.id} className={t.kind === "error" ? "toast error" : "toast"} role="status">
            {t.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
}
